import useConditionalTimeout from 'beautiful-react-hooks/useConditionalTimeout';
import cx from 'classnames';
import Input from 'components/input/Input';
import PageActions from 'components/page-actions/PageActions';
import TimerText from 'components/timer-text/TimerText';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { BE_DEFINES } from 'constants/backend-defines.const';
import { BROADCAST_EVENTS } from 'constants/dom-element.const';
import { I18nNamespace } from 'constants/i18n.const';
import {
  CLOSE_AFTER_TIME_DURATION,
  PAYMENT_DEPOSIT_DEBOUNCE_TIME,
} from 'constants/photo.const';
import KeyboardDrawer from 'containers/selfe-booth/payment/KeyboardDrawer';
import { useCheckPayment } from 'containers/selfe-booth/payment/useCheckPayment';
import { useAppContext } from 'context/AppContext';
import { useSoundContext } from 'context/SoundContext';
import { emitSeqLog } from 'functions/seq-logging.func';
import { formatMoneyVN } from 'helpers/math.helper';
import { isEqualVal } from 'helpers/string.helper';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { usePageTimer } from 'hooks/usePageTimer';
import { SocketEventDataModel } from 'models/photo/socket.model';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useCloseBillAcceptorApiActionMutation,
  useOpenBillAcceptorApiActionMutation,
} from 'store/api/payment.slice-api';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useDebouncedCallback } from 'use-debounce';

function PrintingPayment() {
  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getPrevPath } = useNewPhotoLifeCycleStep();
  const [openBillAcceptorApiAction] = useOpenBillAcceptorApiActionMutation();
  const [closeBillAcceptorApiAction] = useCloseBillAcceptorApiActionMutation();
  const {
    isEnoughDeposit,
    costOfPayment,
    handleBeforeNextWarningAboutMoney,
    handleCheckBeforeCreateQRPayment,
  } = useCheckPayment();

  const { audio } = useSoundContext();
  const { isOnline } = useAppContext();
  const { second, resetTime } = usePageTimer({
    isBeforeTransaction: true,
  });
  // state
  const {
    transactionId,
    depositAmount,
    isChooseQRCodeToPay,
    isMomoTransactionLoading,
  } = useAppSelector((state) => state.selfeApp);
  const { isVerticalScreen } = useAppSelector((s) => s.booth);

  const [keyboardOpen, setKeyboardOpen] = useState<boolean>(false);

  // click Back
  const handleBack = () => {
    audio?.playBackClick?.();
    navigate(getPrevPath() || '');
  };

  // voucher
  const handleShowKeyboard = useCallback(
    (isClose: boolean = false) =>
      () =>
        setKeyboardOpen(!isClose),
    [],
  );

  const handleApplyVoucher = useCallback(
    (voucher: string, voucherValue: number) => {
      const newDepositAmount = depositAmount + voucherValue;
      dispatch(
        setSelfeAppStateAction({
          depositAmount: newDepositAmount,
          appliedVoucherCode: voucher,
          appliedVoucherValue: voucherValue,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [depositAmount, transactionId, dispatch],
  );

  const handleDeposit: any = useDebouncedCallback(
    useCallback(
      async (e: CustomEvent<SocketEventDataModel>) => {
        emitSeqLog?.({
          messageTemplate: `[Socket] Listening Deposit Type: typeHub=${
            e?.detail?.typeHub
          } | Amount hiện tại=${depositAmount} | nạp=${+e?.detail?.data || 0}`,
        });
        if (isEqualVal(e?.detail?.typeHub, BE_DEFINES.HUB_TYPE.BILL_ACCEPTOR)) {
          const newDepositAmount = depositAmount + (+e?.detail?.data || 0);
          audio?.playDeposit?.();
          resetTime();
          dispatch(setSelfeAppStateAction({ depositAmount: newDepositAmount }));
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dispatch, resetTime, transactionId, depositAmount],
    ),
    PAYMENT_DEPOSIT_DEBOUNCE_TIME,
    { trailing: false, leading: true },
  );

  // open bill acceptor session
  useEffect(() => {
    if (transactionId && depositAmount < costOfPayment) {
      openBillAcceptorApiAction({ transactionId });
    }
  }, [
    dispatch,
    openBillAcceptorApiAction,
    transactionId,
    depositAmount,
    costOfPayment,
  ]);

  // close bill acceptor session
  useConditionalTimeout(
    () => closeBillAcceptorApiAction(),
    CLOSE_AFTER_TIME_DURATION,
    depositAmount >= costOfPayment,
  );

  // Add event broadcast bill acceptor
  useEffect(() => {
    document.addEventListener(BROADCAST_EVENTS.BILL_ACCEPTOR, handleDeposit);
    return () => {
      document.removeEventListener(
        BROADCAST_EVENTS.BILL_ACCEPTOR,
        handleDeposit,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderPaymentActions = () => (
    <PageActions
      NextButtonProps={{
        children:
          isChooseQRCodeToPay && !isEnoughDeposit
            ? t('common:qrCode')
            : t('common:continue'),
        disabled: !isChooseQRCodeToPay && (!isEnoughDeposit || !isOnline),
        loading: isMomoTransactionLoading,
      }}
      handleBack={handleBack}
      handleContinue={
        isChooseQRCodeToPay
          ? handleCheckBeforeCreateQRPayment
          : handleBeforeNextWarningAboutMoney
      }
      className={cx({
        'printing-payment__page-actions--vertical': isVerticalScreen,
      })}
    />
  );

  return (
    <>
      <div className="payment">
        <TimerText second={second} />
        {isVerticalScreen && renderPaymentActions()}
        {!isVerticalScreen && (
          <div className="page-title-wrapper mb-[6rem]">
            <Typography
              variant={TYPOGRAPHY_VARIANTS.H1}
              data-text={t(`${I18nNamespace.COMMON}:payment`)}
              className="page-title"
            >
              {t(`${I18nNamespace.COMMON}:payment`)}
            </Typography>
          </div>
        )}
        <div className="printing-payment__form">
          <div className="printing-payment__form-row">
            <div className="text-right printing-payment__field-label">
              <Typography
                className="font-semibold"
                variant={TYPOGRAPHY_VARIANTS.H2}
                firstCapCase
              >
                {t('page:needPayment')}
              </Typography>
            </div>
            <div className="payment-right-group">
              <Input
                className="input-need-pay"
                value={`${formatMoneyVN(costOfPayment)} đ`}
                readOnly
              />
            </div>
          </div>
          <div className="printing-payment__form-row">
            <div className="text-right printing-payment__field-label">
              <Typography
                className="font-semibold"
                variant={TYPOGRAPHY_VARIANTS.H2}
                firstCapCase
              >
                {t('page:deposited')}
              </Typography>
            </div>
            <div className="payment-right-group">
              <Input
                className="input-deposited"
                value={`${formatMoneyVN(depositAmount || 0)} đ`}
                readOnly
              />
            </div>
          </div>
          <div className="printing-payment__form-row change-money-warning">
            <div className="text-right printing-payment__field-label" />
            <div className="payment-right-group">
              <Typography
                className="change-money-warning-text"
                variant={TYPOGRAPHY_VARIANTS.SMALL}
                firstCapCase
              >
                {depositAmount > costOfPayment
                  ? t('page:changeMoneyWarning')
                  : t('page:onlyAcceptSpecifyMoney')}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <KeyboardDrawer
        open={keyboardOpen}
        onClose={handleShowKeyboard(true)}
        onOk={handleApplyVoucher}
      />
      {!isVerticalScreen && renderPaymentActions()}
    </>
  );
}

export default PrintingPayment;
